import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { LuTriangleAlert } from 'react-icons/lu';

type ColumnMismatchAlertProps = {
  listName: string;
  missing: string[];
  unexpected: string[];
  maxVisible?: number;
};

const ColumnPills: FC<{ columns: string[]; maxVisible: number; moreLabel: (count: number) => string }> = ({
  columns,
  maxVisible,
  moreLabel,
}) => {
  const visible = columns.slice(0, maxVisible);
  const overflow = columns.length - visible.length;

  return (
    <div className='mt-2 flex flex-wrap items-center gap-1.5'>
      {visible.map((col) => (
        <span
          key={col}
          className='rounded-md border border-red-300 bg-white px-2 py-0.5 text-xs font-medium text-red-800'
        >
          {col}
        </span>
      ))}
      {overflow > 0 ? <span className='text-xs font-semibold text-red-700'>{moreLabel(overflow)}</span> : null}
    </div>
  );
};

export const ColumnMismatchAlert: FC<ColumnMismatchAlertProps> = ({
  listName,
  missing,
  unexpected,
  maxVisible = 6,
}) => {
  const { t } = useTranslation('comuni');

  return (
    <div role='alert' className='rounded-lg border border-red-200 bg-red-50 px-4 py-3'>
      <div className='flex gap-3'>
        <LuTriangleAlert className='mt-0.5 size-5 flex-none text-amber-500' aria-hidden />
        <div className='min-w-0 flex-1'>
          <p className='text-sm font-bold text-red-800'>{t('import.mismatchTitle')}</p>
          <p className='mt-1 text-sm text-red-700'>{t('import.mismatchBody', { name: listName })}</p>

          {missing.length > 0 ? (
            <div className='mt-3'>
              <p className='text-xs font-bold uppercase tracking-wide text-red-800'>{t('import.mismatchMissing')}</p>
              <ColumnPills
                columns={missing}
                maxVisible={maxVisible}
                moreLabel={(count) => t('import.mismatchMore', { count })}
              />
            </div>
          ) : null}

          {unexpected.length > 0 ? (
            <div className='mt-3'>
              <p className='text-xs font-bold uppercase tracking-wide text-red-800'>{t('import.mismatchUnexpected')}</p>
              <ColumnPills
                columns={unexpected}
                maxVisible={maxVisible}
                moreLabel={(count) => t('import.mismatchMore', { count })}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
